import { Fragment, useState } from "react";
import { Link } from "./Navigation";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ClipboardList,
  Headphones,
  Search,
  ShieldCheck,
  Users,
  Workflow,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { usePageSeo, usePageSeoFor } from "@/lib/seo";
import { PAGE_SEO } from "@/data/pageSeo";
import {
  WHY_US_PRINCIPLES as PRINCIPLES,
  WHY_US_BUYER_QUESTIONS as BUYER_QUESTIONS,
} from "@/data/whyUs";
import { FAQS, FAQ_CATEGORIES } from "@/data/faqs";
import { Shell, Close } from "./Shell";
import { useScrollEngine } from "./useScrollEngine";
import "./information.css";
export { default as About } from "./AboutPage";

// ------------------------------------------------------------------ Why us
const STAGES = [
  {
    name: "Understand",
    line: "The calls you get and the tools you already run.",
    icon: ClipboardList,
    title: "Start with a real day at work.",
    body: "We review the calls you get, the work your team does, and where a customer can get stuck. Bring your current phone, calendar and CRM to that review.",
    fields: [
      ["Bring", "Services, hours, and current tools"],
      ["Decide", "The first problem to solve"],
      ["Leave with", "A defined setup scope"],
    ],
  },
  {
    name: "Configure",
    line: "Your answers, your booking rules, your handoffs.",
    icon: Workflow,
    title: "Give the system your ground rules.",
    body: "Approved answers, booking rules, service areas, and human handoffs turn a general tool into a front office that represents your business.",
    fields: [
      ["Knowledge", "Your approved business information"],
      ["Availability", "Your appointment and booking rules"],
      ["Handoff", "The situations your people handle"],
    ],
  },
  {
    name: "Test",
    line: "Test calls before a customer hears it.",
    icon: Headphones,
    title: "Hear it before your customers do.",
    body: "Test calls help expose unclear answers and awkward transitions. The team reviews those scenarios and tunes the flow before it represents you.",
    fields: [
      ["Try", "Common questions and edge cases"],
      ["Review", "Answers, routing, and booking flow"],
      ["Refine", "Instructions that need more clarity"],
    ],
  },
  {
    name: "Review",
    line: "Records your team can act on and change.",
    icon: Users,
    title: "Keep people in the picture.",
    body: "Your team can review call records and bring changes back into the setup. When the business changes, the instructions and workflow should reflect it.",
    fields: [
      ["See", "Call records and customer context"],
      ["Discuss", "New services or changing processes"],
      ["Update", "The agreed business rules"],
    ],
  },
] as const;

// fora's story layout in one glass panel: the four stages down the left, the
// sheet for the chosen stage on the right, the panel itself never moving.
function SetupStages() {
  const [stage, setStage] = useState(0);
  const s = STAGES[stage];
  return (
    <>
      <div className="sp-head" data-sc-in data-sc-stagger="70">
        <div>
          <p className="g-eyebrow">How setup goes</p>
          <h2 id="wu-setup-heading">Thought through. Then switched on.</h2>
        </div>
        <p className="g-lede">
          A clear path from your first call with us to a front office built
          around your business.
        </p>
      </div>
      <div className="sp-panel g-panel" data-sc-in>
        <div
          className="sp-stages"
          role="group"
          aria-label="Explore setup stages"
        >
          {STAGES.map((p, i) => (
            <button
              key={p.name}
              type="button"
              aria-pressed={i === stage}
              aria-controls="wu-setup-sheet"
              onClick={() => setStage(i)}
            >
              <p.icon size={18} aria-hidden="true" />
              <span>
                <strong>{p.name}</strong>
                <small>{p.line}</small>
              </span>
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          ))}
        </div>
        <div
          id="wu-setup-sheet"
          className="sp-sheet"
          key={stage}
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="sp-sheet-head">
            <span className="g-panel-label">
              <s.icon size={14} aria-hidden="true" /> Stage {stage + 1} of{" "}
              {STAGES.length}
            </span>
            <span className="g-panel-label">Illustrative</span>
          </div>
          <h3 className="g-h3">{s.title}</h3>
          <p>{s.body}</p>
          <dl className="sp-fields">
            {s.fields.map(([term, value]) => (
              <div key={term}>
                <dt>{term}</dt>
                <dd>{value}</dd>
                <span className="g-check" aria-hidden="true">
                  <Check size={12} />
                </span>
              </div>
            ))}
          </dl>
          <Link to="/book" className="g-btn g-btn--small">
            Talk through your setup <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>
    </>
  );
}

export function WhyUs() {
  const seo = PAGE_SEO["/why-us"];
  usePageSeoFor("/why-us");
  useScrollEngine();
  return (
    <Shell>
      <section className="info-head rb-wrap" data-sc-in data-sc-stagger="70">
        <p className="g-eyebrow">Why Chleb</p>
        <h1>{seo.h1}</h1>
        <p className="g-lede">{seo.intro}</p>
        <Link to="/about" className="g-btn g-btn--ghost g-btn--small">
          Meet Dominik & Jake <ArrowUpRight size={16} aria-hidden="true" />
        </Link>
      </section>
      <section
        className="wu-principles rb-wrap"
        aria-labelledby="wu-principles-heading"
      >
        <div className="wu-principles-head" data-sc-in data-sc-stagger="70">
          <h2 id="wu-principles-heading">Five things we hold to.</h2>
          <p className="g-lede">
            What to expect from the people setting it up, before launch and
            after.
          </p>
        </div>
        <ul className="wu-list" data-sc-in data-sc-stagger="60">
          {PRINCIPLES.map(([h, p]) => (
            <li key={h}>
              <h3 className="g-h3">{h}</h3>
              <p>{p}</p>
            </li>
          ))}
        </ul>
      </section>
      <section
        className="wu-setup g-band g-band--morning"
        aria-labelledby="wu-setup-heading"
      >
        <div className="rb-wrap">
          <SetupStages />
        </div>
      </section>
      <section
        className="wu-questions g-tone"
        aria-labelledby="wu-questions-heading"
      >
        <div className="rb-wrap wu-questions-grid">
          <div className="wu-questions-copy" data-sc-in data-sc-stagger="70">
            <h2 id="wu-questions-heading">Good questions. Better decisions.</h2>
            <p>
              If your workflow needs specialized intake or integrations, bring
              that into the conversation early. We’ll review the requirements
              with you before defining the solution.
            </p>
            <Link to="/comparisons" className="g-btn g-btn--ghost g-btn--small">
              Explore your options <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="wu-questions-list" data-sc-in>
            <Accordion type="single" collapsible defaultValue="q-0">
              {BUYER_QUESTIONS.map(([q, a], i) => (
                <AccordionItem
                  key={q}
                  value={`q-${i}`}
                  className="faq-item g-glass"
                >
                  <AccordionTrigger>{q}</AccordionTrigger>
                  <AccordionContent>{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <Close />
    </Shell>
  );
}

// --------------------------------------------------------------------- FAQ
export function Faq() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const seo = PAGE_SEO["/faq"];
  usePageSeoFor("/faq");
  useScrollEngine();
  const query = q.trim().toLowerCase();
  const items = FAQS.filter(
    (f) =>
      !f.gap &&
      (category === "all" || f.category === category) &&
      (!query || `${f.question} ${f.answer}`.toLowerCase().includes(query)),
  );
  // Grouped under their topic labels when every topic is showing, so thirty
  // rows read as seven short lists rather than one long one.
  const groups = FAQ_CATEGORIES.map((c) => ({
    ...c,
    items: items.filter((f) => f.category === c.key),
  })).filter((g) => g.items.length);
  const clear = () => {
    setQ("");
    setCategory("all");
  };
  return (
    <Shell>
      <section className="info-head rb-wrap" data-sc-in data-sc-stagger="70">
        <p className="g-eyebrow">Questions & answers</p>
        <h1>{seo.h1}</h1>
        <p className="g-lede">{seo.intro}</p>
      </section>
      <section
        className="faq g-band g-band--morning"
        aria-label="Questions and answers"
      >
        <div className="rb-wrap faq-grid">
          <aside className="faq-side" data-sc-in>
            <label className="faq-search g-glass--quiet">
              <Search size={17} aria-hidden="true" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search questions"
                aria-label="Search questions"
              />
            </label>
            <div
              className="faq-topics"
              role="radiogroup"
              aria-label="Question topics"
            >
              <button
                type="button"
                role="radio"
                aria-checked={category === "all"}
                onClick={() => setCategory("all")}
              >
                All topics
              </button>
              {FAQ_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={category === c.key}
                  onClick={() => setCategory(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </aside>
          <div className="faq-list" data-sc-in>
            <p className="g-note faq-status" role="status">
              {items.length} questions
            </p>
            {items.length ? (
              <Accordion type="single" collapsible>
                {groups.map((g) => (
                  <Fragment key={g.key}>
                    {category === "all" && (
                      <p className="g-label faq-group-label">{g.label}</p>
                    )}
                    {g.items.map((f) => (
                      <AccordionItem
                        key={f.id}
                        value={f.id}
                        className="faq-item g-glass"
                      >
                        <AccordionTrigger>{f.question}</AccordionTrigger>
                        <AccordionContent>{f.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Fragment>
                ))}
              </Accordion>
            ) : (
              <div className="faq-empty g-panel">
                <h2 className="g-h3">No matching questions.</h2>
                <p>Try a different search, or ask our team.</p>
                <button
                  type="button"
                  className="g-btn g-btn--ghost g-btn--small"
                  onClick={clear}
                >
                  Clear search
                </button>
              </div>
            )}
            <Link to="/contact" className="g-row faq-ask">
              <span>
                <strong>Still have a question?</strong>
                <small>Ask the team. We read every message.</small>
              </span>
              <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
      <Close />
    </Shell>
  );
}

// ------------------------------------------------------ Trust and security
const SAFEGUARDS = [
  [
    "Data safeguards",
    "Chleb designs its services around encrypted transmission and storage, access controls, role-based permissions, and audit logging. The exact setup and responsibilities depend on the service and agreement.",
  ],
  [
    "Healthcare requirements",
    "Chleb does not sign Business Associate Agreements today and is not set up to handle protected health information; a healthcare practice should ask about its setup on a call. Platform safeguards alone do not make an implementation HIPAA compliant. Configuration and customer usage matter.",
  ],
  [
    "Plain claims",
    "HIPAA has no official certification program. Chleb does not claim third-party certification, audit, or accreditation such as SOC 2 or ISO 27001 unless expressly stated in a signed agreement.",
  ],
  [
    "Your records and rights",
    "Review the privacy policy for data collection, retention, sharing, and requests. Your signed agreement identifies the services and contracting entity for your engagement.",
  ],
] as const;

export function Verifications() {
  usePageSeoFor("/verifications");
  useScrollEngine();
  return (
    <Shell>
      <section className="info-head rb-wrap" data-sc-in data-sc-stagger="70">
        <p className="g-eyebrow">Trust & security</p>
        <h1>Clear about how your information is handled.</h1>
        <p className="g-lede">
          Customer conversations deserve care. Understand the safeguards,
          responsibilities, and agreements behind your setup.
        </p>
      </section>
      <section
        className="vf g-band g-band--morning"
        aria-label="Safeguards and agreements"
      >
        <div className="rb-wrap">
          <div className="vf-panel g-panel" data-sc-in>
            <div className="g-panel-head">
              <span className="g-panel-label">
                <ShieldCheck size={15} aria-hidden="true" /> Safeguards and
                agreements
              </span>
              <span className="g-note">Confirmed in your signed agreement</span>
            </div>
            <div className="vf-rows">
              {SAFEGUARDS.map(([h, p]) => (
                <article key={h}>
                  <h3 className="g-h3">{h}</h3>
                  <p>{p}</p>
                </article>
              ))}
            </div>
          </div>
          <div className="vf-links" data-sc-in data-sc-stagger="60">
            <p className="g-label">Read next</p>
            <Link className="g-row" to="/privacy-policy">
              Privacy policy <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
            <Link className="g-row" to="/terms-of-service">
              Terms of service <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
            <Link className="g-row" to="/contact">
              Discuss your requirements{" "}
              <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
      <Close />
    </Shell>
  );
}

// --------------------------------------------------------------------- 404
export function NotFound() {
  usePageSeo({
    title: "Page Not Found | Chleb AI",
    description:
      "Find Chleb AI products, plans, demos, and contact information.",
    path: "/404",
    noindex: true,
  });
  useScrollEngine();
  return (
    <Shell>
      <section className="nf g-band g-band--morning">
        <div className="rb-wrap">
          <div
            className="nf-panel g-panel g-panel--strong"
            data-sc-in
            data-sc-stagger="60"
          >
            <p className="g-eyebrow">Page not found</p>
            <h1>404</h1>
            <p>
              This address doesn’t have a page. Explore our plans or head back
              to Chleb AI.
            </p>
            <div className="rb-actions">
              <Link to="/" className="g-btn">
                Back to home
              </Link>
              <Link to="/pricing" className="g-btn g-btn--ghost">
                See plans
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Shell>
  );
}
